import { useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@overrides/types";
import { GridV2ExplainerBanner } from "../GridV2ExplainerBanner";
import { TenantIconNameEnum } from "@/types/tenantIcons";

/**
 * Points one traveller needs to book with Fixed pricing on Grid V2, by trip
 * kind. The BFF sends no value, so the FE holds it: half of the lowest band in
 * hts-rbc-bff `rewards/fixed-points-pricing-v2.conf` (15,000 round trip,
 * 7,500 one way; multi-city is priced on the round-trip table), the gate
 * `AdditionalPointsCalculator.hasSufficientPoints` applies.
 *
 * Limitation, decided for the A/B test: the gate scales with the seated
 * passengers (a party of two is refused from 15,000 round trip), the banner
 * still shows the one-traveller value.
 */
export const GRID_V2_FIXED_PRICING_MINIMUM_POINTS: Record<
  TripCategory,
  number
> = {
  [TripCategory.ROUND_TRIP]: 7500,
  [TripCategory.ONE_WAY]: 3750,
  [TripCategory.MULTI_CITY]: 7500,
};

export interface GridV2FixedPricingMinimumBannerProps {
  /** The kind of trip searched; sets the minimum shown. */
  tripCategory: TripCategory;
  className?: string;
}

/**
 * Grid V2 explainer for a shopper whose balance is under the Fixed pricing
 * minimum: the results show Flex pricing only, with no tab to switch. The
 * caller decides when it shows.
 */
export const GridV2FixedPricingMinimumBanner = ({
  tripCategory,
  className,
}: GridV2FixedPricingMinimumBannerProps) => {
  const { t, formatNumber } = useI18nContext();

  return (
    <GridV2ExplainerBanner
      icon={TenantIconNameEnum.pointsCreditCard}
      title={t("gridV2.fixedPricingMinimumBanner.title", {
        minimum: formatNumber(
          GRID_V2_FIXED_PRICING_MINIMUM_POINTS[tripCategory]
        ),
      })}
      className={className}
    >
      {t("gridV2.fixedPricingMinimumBanner.body")}
    </GridV2ExplainerBanner>
  );
};
